/**
 * view-role.ts — role resolution helpers for routing and UI (TOG-01).
 *
 * These were relocated here from pdf/layout.ts so the auth layer owns them
 * without a cross-feature dependency on the PDF pipeline.
 *
 * SECURITY NOTE: resolveViewRole governs ONLY client-side rendering and routing.
 * Server-side authorization guards MUST always use the real JWT `rol` claim.
 */

/**
 * App role type. Mirrors `Rol` from auth.service without creating a
 * cross-feature dependency from the auth layer into the domain types.
 */
export type AppRole = "admin" | "technician";

/**
 * Returns the home panel path for the given effective role (TOG-01).
 *
 * Pass the EFFECTIVE role (output of resolveViewRole), not the raw JWT role,
 * so an admin viewing as technician correctly lands on /tecnico.
 *
 * Used by useHomePath() and RootRedirect to keep "volver" routing DRY.
 */
export function homePathForRole(role: AppRole): "/admin" | "/tecnico" {
  return role === "admin" ? "/admin" : "/tecnico";
}

/**
 * Resolves the effective role for UI rendering and routing, applying the
 * view-as downgrade overlay (TOG-01).
 *
 * Rules:
 *  - An admin user MAY view as technician (downgrade allowed).
 *  - A technician can NEVER escalate to admin via the overlay (upgrade blocked).
 *  - No viewAs set (`null`) → returns the real role unchanged.
 *
 * SECURITY NOTE: This function governs ONLY client-side rendering and routing.
 * Server-side authorization guards MUST always use the real JWT `rol` claim.
 */
export function resolveViewRole(realRole: AppRole, viewAs: AppRole | null): AppRole {
  if (realRole === "admin" && viewAs === "technician") return "technician";
  return realRole;
}
